import { useCallback, useEffect, useMemo, useState } from "react";
import { RecordingActivityContext } from "./RecordingActivityContext";

/** How often to refresh the cross-tab activity heartbeat while recording. */
const RECORDING_HEARTBEAT_MS = 30_000;

/**
 * Provides {@link RecordingActivityContext}. Uses a reference counter so
 * overlapping registrations (e.g. React StrictMode double-mount, multiple
 * concurrent recorders) cannot corrupt the active state.
 *
 * While any recording is active, also refreshes `localStorage.lastActivity`
 * on a heartbeat so OTHER same-origin tabs (with their own useSessionTimeout
 * running) observe ongoing activity and do not sign out mid-recording —
 * Firebase signOut in any tab propagates to all and would kill the recording.
 */
export const RecordingActivityProvider = ({ children }: { children: React.ReactNode }) => {
    const [recorderCount, setRecorderCount] = useState(0);

    const setRecordingActive = useCallback((active: boolean) => {
        setRecorderCount((n) => Math.max(0, n + (active ? 1 : -1)));
    }, []);

    useEffect(() => {
        if (recorderCount === 0) return;
        localStorage.setItem('lastActivity', Date.now().toString());
        const id = setInterval(() => {
            localStorage.setItem('lastActivity', Date.now().toString());
        }, RECORDING_HEARTBEAT_MS);
        return () => clearInterval(id);
    }, [recorderCount]);

    const value = useMemo(
        () => ({ isRecordingActive: recorderCount > 0, setRecordingActive }),
        [recorderCount, setRecordingActive],
    );

    return (
        <RecordingActivityContext.Provider value={value}>
            {children}
        </RecordingActivityContext.Provider>
    );
};
